import { createContext, JSX, ReactNode, useContext, useEffect, useState } from 'react';

export type PendingDataCtx = {
  pendingData: unknown;
  hasPendingData: boolean;
  initPendingData: (data: { [key: string]: string | boolean }) => void;
  updatePendingData: (data: { [key: string]: string | boolean }) => void;
  reset: () => void;
};

export const PendingDataContext = createContext<PendingDataCtx>({
  pendingData: {},
  hasPendingData: false,
  initPendingData: () => {},
  updatePendingData: () => {},
  reset: () => {},
});

export const PendingDataProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [initData, setInitData] = useState<{ [key: string]: string | boolean }>({});
  const [pendingData, setPendingData] = useState<{ [key: string]: string | boolean }>({});
  const [hasPendingData, setHasPendingData] = useState(false);

  useEffect(() => {
    setHasPendingData(Object.keys(initData).some((key) => initData[key] !== pendingData[key]));
  }, [pendingData]);

  function initPendingData(data: { [key: string]: string | boolean }) {
    setInitData(data);
  }

  function updatePendingData(data: { [key: string]: string | boolean }) {
    setPendingData({ ...pendingData, ...data });
  }

  function reset() {
    setInitData({});
    setPendingData({});
    setHasPendingData(false);
  }

  return (
    <PendingDataContext.Provider value={{ pendingData, hasPendingData, initPendingData, updatePendingData, reset }}>
      {children}
    </PendingDataContext.Provider>
  );
};

export const usePendingData = (): PendingDataCtx => useContext(PendingDataContext);
